import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { fetchCheckInRecordsForYear } from "./checkInOutUtils";
import { toLocalDateString } from "./dateUtils";

/**
 * ────────────────────────────────────────────────────────────────────────────
 * TEMPORARY check-in based leave accrual.
 *
 * New rule (replaces the old "1 sick + 1 casual per elapsed month" accrual):
 *   • 1 Casual leave for every N working days (default 26)
 *   • 1 Sick   leave for every N working days (default 30)
 *
 * Both thresholds are configurable per organization — see LeaveAccrualConfig
 * and fetchLeaveAccrualConfig below. Admins edit them from
 * Leave Management → Configuration.
 *
 * A "working day" is simply a day the member CHECKED IN. Nothing is filtered
 * out — holidays, Sundays and leave days are not consulted at all; if the
 * member checked in, the day counts.
 *
 * Accrual is per CALENDAR YEAR: the count resets to 0 every 1st of January,
 * so limits start again from zero each year. Within a year the window runs
 * from the later of (1 Jan, date of joining) to the earlier of (today,
 * 31 Dec) — a member who joins mid-year only accrues from their joining date.
 *
 * Flip USE_CHECKIN_BASED_ACCRUAL to false to fall back to the previous
 * month-based accrual without touching any call site.
 * ────────────────────────────────────────────────────────────────────────────
 */
export const USE_CHECKIN_BASED_ACCRUAL = true;

/** Working days required to earn one casual leave, when unconfigured. */
export const DEFAULT_WORKING_DAYS_PER_CASUAL_LEAVE = 26;
/** Working days required to earn one sick leave, when unconfigured. */
export const DEFAULT_WORKING_DAYS_PER_SICK_LEAVE = 30;

/**
 * Per-organization accrual thresholds.
 *
 * Stored at `organization/{orgId}/settings/leaveAccrual` rather than as a
 * field on the organization document itself: the org doc may only be written
 * by its owner, while this settings path is guarded by isOrgAdministrator(),
 * which also covers members holding Admin permission. Keeping it separate
 * means granting them this setting does not also grant write access to the
 * members array or the `admin` field.
 */
export type LeaveAccrualConfig = {
  workingDaysPerCasualLeave: number;
  workingDaysPerSickLeave: number;
};

export const DEFAULT_ACCRUAL_CONFIG: LeaveAccrualConfig = {
  workingDaysPerCasualLeave: DEFAULT_WORKING_DAYS_PER_CASUAL_LEAVE,
  workingDaysPerSickLeave: DEFAULT_WORKING_DAYS_PER_SICK_LEAVE,
};

/**
 * A threshold of 0 or less would divide by zero and hand out infinite leave,
 * so every value is coerced to a whole number of at least 1.
 */
export const sanitizeAccrualConfig = (
  raw?: Partial<LeaveAccrualConfig> | null
): LeaveAccrualConfig => {
  const clamp = (value: unknown, fallback: number) => {
    const n = Math.floor(Number(value));
    return Number.isFinite(n) && n >= 1 ? n : fallback;
  };

  return {
    workingDaysPerCasualLeave: clamp(
      raw?.workingDaysPerCasualLeave,
      DEFAULT_WORKING_DAYS_PER_CASUAL_LEAVE
    ),
    workingDaysPerSickLeave: clamp(
      raw?.workingDaysPerSickLeave,
      DEFAULT_WORKING_DAYS_PER_SICK_LEAVE
    ),
  };
};

const accrualConfigRef = (organizationId: string) =>
  doc(db, `organization/${organizationId}/settings/leaveAccrual`);

/** Read the organization's accrual thresholds, falling back to the defaults. */
export const fetchLeaveAccrualConfig = async (
  organizationId?: string
): Promise<LeaveAccrualConfig> => {
  if (!organizationId) return DEFAULT_ACCRUAL_CONFIG;
  try {
    const snap = await getDoc(accrualConfigRef(organizationId));
    if (!snap.exists()) return DEFAULT_ACCRUAL_CONFIG;
    return sanitizeAccrualConfig(snap.data() as Partial<LeaveAccrualConfig>);
  } catch (err) {
    console.error("[leaveAccrual] Failed to fetch accrual config:", err);
    return DEFAULT_ACCRUAL_CONFIG;
  }
};

/**
 * Persist the organization's accrual thresholds. Applies everywhere the
 * accrual is computed, for every member of the organization.
 */
export const saveLeaveAccrualConfig = async (
  organizationId: string,
  config: Partial<LeaveAccrualConfig>
): Promise<LeaveAccrualConfig> => {
  const clean = sanitizeAccrualConfig(config);
  await setDoc(accrualConfigRef(organizationId), clean, { merge: true });
  return clean;
};

export type LeaveAccrual = {
  /** Calendar year this accrual was computed for. */
  year: number;
  /** Days the member checked in on within that year. */
  workingDays: number;
  /** Accrued casual leaves — floor(workingDays / casual threshold). */
  casual: number;
  /** Accrued sick leaves — floor(workingDays / sick threshold). */
  sick: number;
  /** Total paid pool — casual + sick. */
  paid: number;
  /** Working days still needed before the next casual leave is earned. */
  workingDaysToNextCasual: number;
  /** Working days still needed before the next sick leave is earned. */
  workingDaysToNextSick: number;
};

export const EMPTY_ACCRUAL: LeaveAccrual = {
  year: new Date().getFullYear(),
  workingDays: 0,
  casual: 0,
  sick: 0,
  paid: 0,
  workingDaysToNextCasual: DEFAULT_WORKING_DAYS_PER_CASUAL_LEAVE,
  workingDaysToNextSick: DEFAULT_WORKING_DAYS_PER_SICK_LEAVE,
};

/**
 * Normalise the many shapes a joining date arrives in (ISO string,
 * Firestore Timestamp, Date) into a local Date at midnight.
 */
export const parseJoiningDate = (raw: unknown): Date | null => {
  if (!raw) return null;

  let dateObj: Date;
  if (raw instanceof Date) {
    dateObj = raw;
  } else if (typeof raw === "object" && (raw as any).seconds !== undefined) {
    dateObj = new Date((raw as any).seconds * 1000);
  } else {
    dateObj = new Date(raw as string);
  }

  if (isNaN(dateObj.getTime())) return null;
  return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
};

/**
 * The date window a member accrues over for a given calendar year.
 *
 * Starts on 1 January (or the joining date, if they joined during the year)
 * and ends today (or 31 December, for a year that has already finished).
 * Returns null when the year produces no window at all — a year that ended
 * before the member joined, or one that has not started yet.
 */
export const getAccrualWindow = (
  joiningDate: Date,
  year: number,
  asOf: Date = new Date()
): { start: Date; end: Date } | null => {
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);

  const start = joiningDate > yearStart ? joiningDate : yearStart;
  const end = asOf < yearEnd ? asOf : yearEnd;

  if (end < start) return null;
  return { start, end };
};

/** Turn a qualifying working-day count into accrued leave limits. */
export const accrualFromWorkingDays = (
  workingDays: number,
  year: number = new Date().getFullYear(),
  config: LeaveAccrualConfig = DEFAULT_ACCRUAL_CONFIG
): LeaveAccrual => {
  const { workingDaysPerCasualLeave, workingDaysPerSickLeave } =
    sanitizeAccrualConfig(config);
  const safeDays = Math.max(0, Math.floor(workingDays));
  const casual = Math.floor(safeDays / workingDaysPerCasualLeave);
  const sick = Math.floor(safeDays / workingDaysPerSickLeave);

  return {
    year,
    workingDays: safeDays,
    casual,
    sick,
    paid: casual + sick,
    workingDaysToNextCasual:
      workingDaysPerCasualLeave - (safeDays % workingDaysPerCasualLeave),
    workingDaysToNextSick:
      workingDaysPerSickLeave - (safeDays % workingDaysPerSickLeave),
  };
};

/**
 * Extra leave days an admin granted on top of the accrual.
 *
 * Stored as a delta, not a total: if a member has accrued 10 casual and the
 * admin sets the limit to 15, the add-on is 5. As the accrual keeps growing
 * the 5 rides along on top of it, which is the point of storing the
 * difference rather than the absolute number.
 */
export type LeaveLimitAddons = {
  paid: number;
  sick: number;
  casual: number;
};

export const EMPTY_ADDONS: LeaveLimitAddons = { paid: 0, sick: 0, casual: 0 };

/**
 * Work out the add-on from what the admin typed against what is accrued.
 *
 * Only increases are stored — entering a number at or below the accrued
 * figure clears the add-on rather than recording a negative, since a negative
 * would silently eat into future accrual.
 */
export const computeLimitAddons = (
  entered: { sick: number; casual: number },
  accrued: { sick: number; casual: number }
): LeaveLimitAddons => {
  const sick = Math.max(0, Math.round((entered.sick || 0) - accrued.sick));
  const casual = Math.max(0, Math.round((entered.casual || 0) - accrued.casual));
  // Paid is always the sum of the two — it is never edited on its own.
  return { sick, casual, paid: sick + casual };
};

/** Accrued limits plus whatever the admin granted on top. */
export const applyAddons = (
  accrued: { paid: number; sick: number; casual: number },
  addons: LeaveLimitAddons = EMPTY_ADDONS
): { paid: number; sick: number; casual: number } => ({
  paid: accrued.paid + (addons.paid || 0),
  sick: accrued.sick + (addons.sick || 0),
  casual: accrued.casual + (addons.casual || 0),
});

/**
 * Count the days a member checked in on, inside an inclusive date window.
 * Every distinct check-in date counts — no holiday, Sunday or leave filtering.
 */
export const computeWorkingDaysInWindow = async (
  organizationId: string,
  userId: string,
  start: Date,
  end: Date
): Promise<number> => {
  const startKey = toLocalDateString(start);
  const endKey = toLocalDateString(end);

  // Check-ins are partitioned per year. The window never spans more than one
  // calendar year today, but the loop keeps this correct if that ever changes.
  const years: number[] = [];
  for (let y = start.getFullYear(); y <= end.getFullYear(); y++) years.push(y);

  const checkInsPerYear = await Promise.all(
    years.map((y) =>
      fetchCheckInRecordsForYear(organizationId, userId, y).catch((err) => {
        console.error(`[leaveAccrual] check-ins failed for ${y}:`, err);
        return [];
      })
    )
  );

  const checkedInDays = new Set<string>();
  checkInsPerYear.flat().forEach((record: any) => {
    const raw = record?.checkInTime;
    if (!raw) return;
    const dt = new Date(raw);
    if (isNaN(dt.getTime())) return;

    const key = toLocalDateString(dt);
    if (key < startKey || key > endKey) return;
    checkedInDays.add(key);
  });

  return checkedInDays.size;
};

/**
 * Full accrual for a member in one calendar year: fetches their check-ins and
 * applies the 26-working-days-per-casual / 30-working-days-per-sick rule.
 *
 * The count is scoped to `year`, so every 1st of January the limits start
 * again from zero.
 *
 * A member with no joining date on record (older admin accounts predate the
 * field) accrues from 1 January — they have clearly been around at least that
 * long, and returning 0 forever would be worse.
 */
export const computeAccruedLeaveLimits = async (
  organizationId?: string,
  userId?: string,
  joiningDateRaw?: unknown,
  options: { year?: number; asOf?: Date; config?: LeaveAccrualConfig } = {}
): Promise<LeaveAccrual> => {
  const asOf = options.asOf ?? new Date();
  const year = options.year ?? asOf.getFullYear();

  // Callers that already hold the org config pass it in; otherwise read it.
  const config =
    options.config ?? (await fetchLeaveAccrualConfig(organizationId));

  // Zero working days, but with this org's thresholds in the "next leave in"
  // counters rather than the built-in defaults.
  const emptyForYear = accrualFromWorkingDays(0, year, config);

  if (!organizationId || !userId) return emptyForYear;

  // Falling back to 1 Jan of the target year makes the window the whole year.
  const joiningDate = parseJoiningDate(joiningDateRaw) ?? new Date(year, 0, 1);

  const window = getAccrualWindow(joiningDate, year, asOf);
  if (!window) return emptyForYear;

  try {
    const workingDays = await computeWorkingDaysInWindow(
      organizationId,
      userId,
      window.start,
      window.end
    );
    return accrualFromWorkingDays(workingDays, year, config);
  } catch (err) {
    console.error("[leaveAccrual] Failed to compute accrued limits:", err);
    return emptyForYear;
  }
};
